// ──────────────────────────────────────────────────────────────────────────
// guest.js — localStorage-backed store for pre-auth users.
//
// Shape mirrors the server exactly (snake_case where the server uses it).
// Keyed by `proxera.guest.v1`. Never touched once the user signs in — the
// migration routine in `guestMigration.js` uploads it and then clears the key.
// ──────────────────────────────────────────────────────────────────────────

import { v4 as uuidv4 } from 'uuid';

const KEY = 'proxera.guest.v1';
const MIGRATED_KEY = 'proxera.guest.migrated';

function emptyStore() {
  return {
    habits: [],       // {id, name, icon, accent, frequency, custom_days, sort_order, end_year, end_month, streak, longest, created_at}
    checkins: [],     // {habit_id, date (ISO), status}
    briefings: {},    // { 'YYYY-MM-DD': { tasks: [...], focus, calls, date_display } }
    goals: [],        // {id, kind, period, text, checked, sort_order}
    profile: { displayName: null },
  };
}

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw);
    return { ...emptyStore(), ...parsed };
  } catch {
    return emptyStore();
  }
}

function write(next) {
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch {}
}

function mapHabitOut(r) {
  return {
    id: r.id,
    name: r.name,
    icon: r.icon,
    accent: r.accent,
    frequency: r.frequency,
    customDays: r.custom_days,
    sortOrder: r.sort_order,
    endYear: r.end_year,
    endMonth: r.end_month,
    streak: r.streak,
    longest: r.longest,
    createdAt: r.created_at,
  };
}

export const guestStore = {
  // ---- Habits ----
  async listHabits() {
    const s = read();
    return [...s.habits]
      .sort((a, b) => (a.sort_order - b.sort_order) || String(a.created_at).localeCompare(String(b.created_at)))
      .map(mapHabitOut);
  },
  async createHabit(row) {
    const s = read();
    const full = {
      id: row.id || uuidv4(),
      name: row.name,
      icon: row.icon,
      accent: row.accent,
      frequency: row.frequency || 'Daily',
      custom_days: row.custom_days || null,
      sort_order: row.sort_order ?? s.habits.length,
      end_year: row.end_year ?? null,
      end_month: row.end_month ?? null,
      streak: row.streak ?? 0,
      longest: row.longest ?? 0,
      created_at: new Date().toISOString(),
    };
    s.habits.push(full);
    write(s);
    return mapHabitOut(full);
  },
  async updateHabit(id, patch) {
    const s = read();
    const idx = s.habits.findIndex(h => h.id === id);
    if (idx === -1) return null;
    s.habits[idx] = { ...s.habits[idx], ...patch };
    write(s);
    return mapHabitOut(s.habits[idx]);
  },
  async deleteHabit(id) {
    const s = read();
    s.habits = s.habits.filter(h => h.id !== id);
    s.checkins = s.checkins.filter(c => c.habit_id !== id);
    write(s);
  },
  async reorderHabits(idsInOrder) {
    const s = read();
    const order = new Map(idsInOrder.map((id, i) => [id, i]));
    s.habits.forEach(h => { if (order.has(h.id)) h.sort_order = order.get(h.id); });
    write(s);
  },

  // ---- Check-ins ----
  async listCheckins(fromIso, toIso) {
    const s = read();
    return s.checkins
      .filter(c => (!fromIso || c.date >= fromIso) && (!toIso || c.date <= toIso))
      .map(c => ({ habitId: c.habit_id, date: c.date, status: c.status }));
  },
  async setCheckin(habitId, isoDate, status = 'done') {
    const s = read();
    const existing = s.checkins.find(c => c.habit_id === habitId && c.date === isoDate);
    if (existing) existing.status = status;
    else s.checkins.push({ habit_id: habitId, date: isoDate, status });
    write(s);
  },
  async clearCheckin(habitId, isoDate) {
    const s = read();
    s.checkins = s.checkins.filter(c => !(c.habit_id === habitId && c.date === isoDate));
    write(s);
  },

  // ---- Briefings ----
  async getBriefing(isoDate) {
    const s = read();
    const b = s.briefings[isoDate];
    if (!b) return { tasks: [], focus: null, calls: [], date_display: null };
    return {
      tasks: (b.tasks || []).map((t, i) => ({
        id: t.id || `gt-${isoDate}-${i}`,
        text: t.text,
        checked: !!t.checked,
        priority: t.priority || 'MED',
        sortOrder: i,
      })),
      focus: b.focus || null,
      calls: b.calls || [],
      date_display: b.date_display || null,
    };
  },
  async upsertBriefing(isoDate, { tasks, focus, calls, date_display }) {
    const s = read();
    s.briefings[isoDate] = {
      tasks: (tasks || []).map((t, i) => ({
        id: t.id || uuidv4(),
        text: t.text,
        checked: !!t.checked,
        priority: (t.priority || 'MED').toUpperCase(),
        sort_order: i,
      })),
      focus: focus || null,
      calls: calls || [],
      date_display: date_display || null,
    };
    write(s);
  },
  async updateBriefingTask(id, patch) {
    const s = read();
    for (const d of Object.keys(s.briefings)) {
      const list = s.briefings[d].tasks || [];
      const t = list.find(x => x.id === id);
      if (t) {
        if ('text' in patch) t.text = patch.text;
        if ('checked' in patch) t.checked = patch.checked;
        if ('priority' in patch) t.priority = (patch.priority || 'MED').toUpperCase();
        write(s);
        return;
      }
    }
  },
  async deleteBriefingTask(id) {
    const s = read();
    for (const d of Object.keys(s.briefings)) {
      s.briefings[d].tasks = (s.briefings[d].tasks || []).filter(t => t.id !== id);
    }
    write(s);
  },

  // ---- Goals ----
  async listGoals() {
    const s = read();
    return s.goals.map(g => ({
      id: g.id, kind: g.kind, period: g.period, text: g.text, checked: !!g.checked, sortOrder: g.sort_order,
    }));
  },
  async createGoal({ kind, period, text }) {
    const s = read();
    const row = { id: uuidv4(), kind, period, text, checked: false, sort_order: s.goals.length };
    s.goals.push(row);
    write(s);
    return { id: row.id, kind: row.kind, period: row.period, text: row.text, checked: false, sortOrder: row.sort_order };
  },
  async updateGoal(id, patch) {
    const s = read();
    const g = s.goals.find(x => x.id === id);
    if (!g) return;
    if ('text' in patch) g.text = patch.text;
    if ('checked' in patch) g.checked = patch.checked;
    if ('sortOrder' in patch) g.sort_order = patch.sortOrder;
    if ('period' in patch) g.period = patch.period;
    write(s);
  },
  async deleteGoal(id) {
    const s = read();
    s.goals = s.goals.filter(x => x.id !== id);
    write(s);
  },

  // ---- Profile ----
  async getProfile() {
    const s = read();
    return { id: 'guest', displayName: s.profile?.displayName || null, createdAt: null };
  },
  async updateProfile(patch) {
    const s = read();
    s.profile = { ...s.profile, ...patch };
    write(s);
    return { id: 'guest', displayName: s.profile.displayName, createdAt: null };
  },

  // ---- Migration helpers ----
  _readRaw: read,
  _clear() {
    try { localStorage.removeItem(KEY); } catch {}
  },
  _markMigrated(userId) {
    try { localStorage.setItem(MIGRATED_KEY, userId); } catch {}
  },
  _migratedFor() {
    try { return localStorage.getItem(MIGRATED_KEY); } catch { return null; }
  },
  KEY, MIGRATED_KEY,
};
